import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Minus, Play, Plus as PlusIcon, Star } from "lucide-react";
import heroVideo from "../assets/figma/training.gif";
import heroStill from "../assets/figma/hero.png";
import { Brand, Container, SiteFooter, SiteHeader } from "./SiteChrome";
import { ContactSection } from "./ContactSection";
import { api } from "../services/apiService";
import { useCmsBatch } from "../hooks/useCmsBatch";
import { CmsIcon } from "../utils/cmsIcons";

const offersDefault = [
  { title: "Player Assessments", body: "Objective testing across nine criteria and three development domains, turning performance into a clear development plan.", link: "#assessments" },
  { title: "Memberships", body: "Flexible access to elite training environments, specialist coaches and measurable progress.", link: "/memberships" },
  { title: "Field Rentals", body: "Premium, weather-proof turf arenas available for teams, coaches and private sessions.", link: "/rentals" },
  { title: "Leagues & Tournaments", body: "Fast-paced, well-organized competition for youth and adult players at every level.", link: "/events" },
  { title: "Birthday Parties", body: "High-energy soccer experiences planned and hosted by our Formula team.", link: "/events" },
  { title: "Other Programs & Events", body: "Camps, pickup games, corporate events and targeted Formula Fit programs.", link: "/events" },
  { title: "Rehabilitation & Recovery", body: "Data-led return-to-play programs that rebuild confidence, capacity and control.", link: "#rehabilitation" },
];
const metrics = [
  ["Speed & explosiveness", 82],
  ["Agility & direction", 74],
  ["Technical execution", 91],
  ["Decision making", 68],
  ["Game application", 77],
  ["Consistency", 85],
];
const assessmentStatsDefault = [
  { value: "1000s", title: "Athletes benchmarked" },
  { value: "9", title: "Criteria across three domains" },
  { value: "1:1", title: "Unbiased comparison" },
];
const programsDefault = [
  { icon: "Trophy", title: "Youth leagues & tournaments", body: "High-energy, organized competitions for young players looking to test their skills and grow.", link: "/events" },
  { icon: "Users", title: "Adult leagues & tournaments", body: "Fun, competitive, and social divisions designed for players of all experience levels.", link: "/events" },
  { icon: "CalendarDays", title: "Camps", body: "Focused, high-intensity training programs during school breaks to sharply elevate your game.", link: "/events" },
  { icon: "Swords", title: "Pickup games and friendlies", body: "Drop-in sessions for anyone looking to lace up, jump into a match, and play on their own terms.", link: "/events" },
  { icon: "Building2", title: "Corporate and special events", body: "Private access to our world-class courts and spaces for team practices, birthday parties, or events.", link: "/events" },
  { icon: "Dumbbell", title: "Formula Fit", body: "Specialized fitness programs tailored to boost the agility, speed, and endurance of soccer athletes.", link: "/programs" },
];
const rehabDefault = [
  { icon: "Gauge", title: "The Kinetic Chain", body: "We assess above and below the injury to solve the mechanical cause, not only the symptom." },
  { icon: "ShieldCheck", title: "Deceleration as a Weapon", body: "Eccentric strength creates the structural brakes explosive athletes need." },
  { icon: "Swords", title: "The RTP Gap", body: "Wearable data helps us bridge controlled rehab and the chaos of competition." },
  { icon: "BrainCircuit", title: "The Mental Milestone", body: "Progressive exposure rebuilds the trust required to attack the field without hesitation." },
];
const articlesDefault = [
  { value: "Training", title: "Inside the lab: how we turn raw movement into a skill baseline", body: "06 min read" },
  { value: "Community", title: "Talent ID Day: the standout performers and what we learned", body: "04 min read" },
  { value: "Programs", title: "What to expect from your first Formula assessment", body: "05 min read" },
];
const CMS_KEYS = ["home-hero", "home-analytics", "home-offers", "home-assessments", "home-memberships", "home-programs", "home-rehabilitation", "home-lounge"];
const Arrow = () => (
  <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden="true" />
);
const Plus = ({ open }) => (
  <span className={`plus ${open ? "is-open" : ""}`} aria-hidden="true">
    {open ? <Minus size={18} strokeWidth={1.25} /> : <PlusIcon size={18} strokeWidth={1.25} />}
  </span>
);
const Eyebrow = ({ number, children, lime = false }) => (
  <p className={`eyebrow ${lime ? "lime" : ""}`}>
    <span>{number}</span>
    {children}
  </p>
);
const HexO = () => (
  <svg className="hex-o" viewBox="0 0 100 100" width="0.85em" height="0.85em" aria-hidden="true">
    <polygon points="50,4 94,27 94,73 50,96 6,73 6,27" fill="currentColor" />
  </svg>
);
const CtaLink = ({ to, className, children }) => (
  to.startsWith("/") ? (
    <Link to={to} className={className}>{children}</Link>
  ) : (
    <a href={to} className={className}>{children}</a>
  )
);

function Hero({ content }) {
  const title = content.title || "The science of soccer development";
  const body = content.body || "We use empirical evidence, data analytics and neuroscience muscular conditioning to unlock an athlete's maximum potential.";
  return (
    <section className="hero" id="home">
      <img
        className="hero__media"
        src={content.mediaUrl || heroVideo}
        alt="Young athlete training in Formula Soccer's performance lab"
      />
      <div className="hero__shade" />
      <Container className="hero__content">
        <div className="hero__brand">
          <Brand />
        </div>
        <h1>{title}</h1>
        <div className="hero__bottom">
          <div>
            <p>
              {body}
            </p>
            <div className="actions">
              <a className="button button--lime" href="#assessments">
                <span>Find your f<HexO />rmula</span> <Arrow />
              </a>
              <Link className="button button--ghost" to="/contact">
                Book your assessment
              </Link>
              <a className="watch" href="#analytics">
                <span aria-hidden="true"><Play size={15} strokeWidth={1.5} fill="currentColor" /></span>
                <b>Watch now</b>
              </a>
            </div>
          </div>
        </div>
      </Container>
      <div className="hero__index">
        FORMULA / PERFORMANCE SYSTEM 
        {/* <span>01—07</span> */}
      </div>
    </section>
  );
}
function Analytics({ content }) {
  const title = content.title || "Data analytics";
  const subtitle = content.subtitle || "Every player has untapped potential. We make it visible.";
  const body = content.body || "Our performance lab replaces guesswork with objective evaluation. Track your progress, understand your game and train with purpose—whether you play on weekends or plan to go pro.";
  return (
    <section className="section light" id="analytics">
      <Container className="grid-12 analytics">
        <div className="section-copy">
          <Eyebrow number="01">Formula soccer center</Eyebrow>
          <h2>{title}</h2>
          <p className="lead">
            {subtitle}
          </p>
          <p>
            {body}
          </p>
        </div>
        <div className="metric-panel">
          <div className="metric-panel__head">
            <span>PLAYER / 0042</span>
            <span>PERFORMANCE PROFILE</span>
          </div>
          {metrics.map(([label, value]) => (
            <div className="metric" key={label}>
              <span>{label}</span>
              <div>
                <i style={{ width: `${value}%` }} />
              </div>
              <b>{value}</b>
            </div>
          ))}
          <div className="metric-panel__foot">
            <span>LAST TEST 08.26</span>
            <span>↑ 12% SINCE BASELINE</span>
          </div>
        </div>
      </Container>
    </section>
  );
}
function Offers({ content }) {
  const [active, setActive] = useState(0);
  const title = content.title || "What we offer";
  const offers = content.items?.length ? content.items : offersDefault;
  return (
    <section className="section dark" id="programs">
      <Container>
        <Eyebrow number="02" lime>
          Formula soccer
        </Eyebrow>
        <h2>{title}</h2>
        <div className="offer-list">
          {offers.map(({ title: itemTitle, body, link }, index) => (
            <article
              className={`offer ${active === index ? "active" : ""}`}
              key={itemTitle}
            >
              <button
                onClick={() => setActive(active === index ? -1 : index)}
                aria-expanded={active === index}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{itemTitle}</h3>
                <Plus open={active === index} />
              </button>
              <div className="offer__body">
                <p>{body}</p>
                <CtaLink to={link || "/"}>
                  Learn more <Arrow />
                </CtaLink>
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}
function Assessments({ content }) {
  const title = content.title || "Proven player scouting & evaluation";
  const body = content.body || "Our specialist testing protocols establish clear skill baselines. That data becomes your training blueprint—a precise view of what is working and exactly where to improve.";
  const stats = content.items?.length ? content.items : assessmentStatsDefault;
  const pts = [
    [20, 310],
    [100, 270],
    [180, 282],
    [270, 160],
    [355, 190],
    [450, 62],
    [535, 105],
    [575, 25],
  ];
  return (
    <section className="section light" id="assessments">
      <Container className="grid-12 assessment">
        <div className="chart-card">
          <div className="chart-grid" />
          <svg
            viewBox="0 0 580 360"
            preserveAspectRatio="none"
            aria-label="Player performance improving across repeat assessments"
          >
            <polyline points="20,310 100,270 180,282 270,160 355,190 450,62 535,105 575,25" />
            <g>
              {pts.map(([x, y], i) => (
                <circle cx={x} cy={y} r="5" key={i} />
              ))}
            </g>
          </svg>
          <div className="chart-card__labels">
            <span>BASELINE</span>
            <b>+34.8%</b>
            <span>CURRENT</span>
          </div>
        </div>
        <div className="section-copy assessment__copy">
          <Eyebrow number="03">Assessments</Eyebrow>
          <h2>{title}</h2>
          <p>
            {body}
          </p>
          <div className="stats">
            {stats.map((stat) => (
              <div key={stat.title}>
                <b>{stat.value}</b>
                <span>{stat.title}</span>
              </div>
            ))}
          </div>
          <Link className="button button--dark" to="/contact">
            Book your assessment <Arrow />
          </Link>
        </div>
      </Container>
    </section>
  );
}
function Memberships({ introContent, programsContent }) {
  const introTitle = introContent.title || "Formula memberships";
  const kicker = introContent.subtitle || "Take control of your training journey";
  const introBody = introContent.body || "Train how you want, when you want, and as much as you need. Your membership unlocks unlimited training sessions with completely flexible scheduling inside one of the world's most advanced soccer facilities.\n\nOur certified coaching team uses a purpose-driven training approach backed by the latest sports science — together, we create an immersive, data-rich experience that helps you clearly track your progress and take your game to the next level.";
  const programsTitle = programsContent.title || "Beyond the lab";
  const programsSubtitle = programsContent.subtitle || "Discover a program tailored to your goals and schedule";
  const programs = programsContent.items?.length ? programsContent.items : programsDefault;

  return (
    <section className="section dark memberships" id="memberships">
      <Container>
        <Eyebrow number="04" lime>
          Memberships
        </Eyebrow>
        <h2>Memberships</h2>
        <div className="grid-12 membership-intro">
          <h3>
            {introTitle}
          </h3>
          <div>
            <p className="kicker">{kicker}</p>
            {introBody.split("\n\n").filter(Boolean).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
            <div className="age-grid">
              {["U3", "4—5", "6—8", "9—11", "12—19", "Adult"].map((age, i) => (
                <div key={age}>
                  <b>{age}</b>
                  <span>{i === 5 ? "all levels" : "years"}</span>
                </div>
              ))}
            </div>
            <Link className="button button--lime" to="/memberships">
              See membership plans <Arrow />
            </Link>
          </div>
        </div>
        <div className="program-heading">
          <p>{programsSubtitle}</p>
          <h3>{programsTitle}</h3>
        </div>
        <div className="program-grid">
          {programs.map(({ icon, title, body, link }, i) => (
            <article key={title}>
              <span>0{i + 1}</span>
              <div className="program-icon"><CmsIcon name={icon} size={30} strokeWidth={1.25} aria-hidden="true" /></div>
              <h4>{title}</h4>
              <p>{body}</p>
              <CtaLink to={link || "/programs"}>
                Learn more <Arrow />
              </CtaLink>
            </article>
          ))}
        </div>
        <div className="arena-strip">
          <img src={heroStill} alt="Formula Soccer training arena" />
          <p>
            More touches on the ball. More shots on goal. More of the game you
            love.
          </p>
        </div>
        <div className="offerings-foot">
          <p>Join the exciting action—we guarantee more touches on the ball, more shots on goal, and more fun. Every single game is played inside our premium, weather-proof Turf Arenas.</p>
          <span>ADULT LEAGUES · CAMPS · OPEN PLAY · FACILITY RENTALS</span>
        </div>
      </Container>
    </section>
  );
}
function Rehabilitation({ content }) {
  const title = content.title || "Rebuild your engine. Rebuild your mind.";
  const body = content.body || "We do not patch injuries—we engineer durable athletes and bridge the gap between the clinic and competition.";
  const rehab = content.items?.length ? content.items : rehabDefault;
  return (
    <section className="rehab dark" id="rehabilitation">
      <Container>
        <div className="rehab-grid">
          <div className="rehab__intro">
            <Eyebrow number="05" lime>
              Rehabilitation & recovery
            </Eyebrow>
            <h2>
              {title}
            </h2>
            <p>
              {body}
            </p>
            <Link className="button button--lime" to="/contact">
              Schedule a consultation <Arrow />
            </Link>
          </div>
          {rehab.map(({ icon, title: itemTitle, body: itemBody }, i) => (
            <article key={itemTitle}>
              <span>0{i + 1}</span>
              <i><CmsIcon name={icon} size={26} strokeWidth={1.25} aria-hidden="true" /></i>
              <h3>{itemTitle}</h3>
              <p>{itemBody}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}
function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/cms/testimonials")
      .then((response) => setTestimonials(response.data.testimonials || []))
      .catch(() => setTestimonials([]));
  }, []);

  if (!testimonials.length) return null;

  return (
    <section className="section light" id="testimonials">
      <Container>
        <Eyebrow number="06">What families are saying</Eyebrow>
        <h2>Real progress, in their words.</h2>
        <div className="testimonial-grid">
          {testimonials.map((t) => (
            <article key={t._id} className="testimonial-card">
              <div className="testimonial-card-stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill={i < t.rating ? "currentColor" : "none"} strokeWidth={1.5} />
                ))}
              </div>
              <p className="testimonial-card-quote">&ldquo;{t.quote}&rdquo;</p>
              <p className="testimonial-card-author">{t.author}{t.playerAge && `, age ${t.playerAge}`}</p>
              {t.role && <p className="testimonial-card-role">{t.role}</p>}
              {t.outcome && <span className="testimonial-card-outcome">{t.outcome}</span>}
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}
function Partners() {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/partners")
      .then((response) => setPartners(response.data.partners || []))
      .catch(() => setPartners([]));
  }, []);

  if (!partners.length) return null;

  return (
    <section className="partner-strip">
      <Container>
        <p className="partner-strip-label">Trusted by</p>
        <div className="partner-strip-logos">
          {partners.map((p) => (
            p.websiteUrl ? (
              <a key={p._id} href={p.websiteUrl} target="_blank" rel="noreferrer" title={p.name}>
                <img src={p.logoUrl} alt={p.name} />
              </a>
            ) : (
              <img key={p._id} src={p.logoUrl} alt={p.name} title={p.name} />
            )
          ))}
        </div>
      </Container>
    </section>
  );
}
function Lounge({ content }) {
  const title = content.title || "News, ideas and updates";
  const articles = content.items?.length ? content.items : articlesDefault;
  return (
    <section className="section light" id="lounge">
      <Container>
        <div className="section-heading">
          <div>
            <Eyebrow number="07">Formula lounge</Eyebrow>
            <h2>
              {title}
            </h2>
          </div>
          <a className="button button--outline" href="#articles">
            Visit the lounge <Arrow />
          </a>
        </div>
        <div className="article-grid" id="articles">
          {articles.map(({ value: cat, title: articleTitle, body: time }) => (
            <article key={articleTitle}>
              <p>{cat}</p>
              <h3>{articleTitle}</h3>
              <a href="#contact">
                {time} <Arrow />
              </a>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}
export const Homepage = () => {
  const location = useLocation();
  const { content, loaded } = useCmsBatch(CMS_KEYS);

  useEffect(() => {
    if (!location.hash || !loaded) return;
    const id = location.hash.slice(1);
    // Images and the separately-fetched testimonials/partners sections can still be loading and
    // shifting page height after this fires, so the scroll is corrected a couple more times
    // shortly after rather than trusting a single snapshot position.
    const attempts = [0, 250, 800];
    const timers = attempts.map((delay) => setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, delay));
    return () => timers.forEach(clearTimeout);
  }, [location, loaded]);

  const get = (key) => content[key] || {};

  return (
    <>
      <SiteHeader />
      <main>
        <Hero content={get("home-hero")} />
        <Analytics content={get("home-analytics")} />
        <Offers content={get("home-offers")} />
        <Assessments content={get("home-assessments")} />
        <Memberships introContent={get("home-memberships")} programsContent={get("home-programs")} />
        <Rehabilitation content={get("home-rehabilitation")} />
        <Testimonials />
        <Partners />
        <Lounge content={get("home-lounge")} />
        <ContactSection />
      </main>
      <SiteFooter />
    </>
  );
};
